import { FC, useState } from 'react';

import { uniqBy } from 'lodash';

import {
  CreateEntertainerQuotePayload,
  EntertainerQuoteReview,
  UpdateEntertainerQuotePayload,
} from '@admin/queries/AdminEntertainerQuotesQueries';

import { Entertainer } from '@queries/EntertainerQueries';

import Button from '@components/buttons/Button';
import { ImageUploadField } from '@components/form/ImageUploadField';
import { Input } from '@components/form/Input';
import { Textarea } from '@components/form/Textarea';
import { AsyncImage } from '@components/layout/AsyncImg';
import { HorizontalScroll } from '@components/layout/HorizontalScroll/HorizontalScroll';
import { Popup } from '@components/layout/Popup/Popup';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

export const transformEntertainerToQuotePhotoOptions = (
  entertainer: Entertainer,
): QuotePhotoOption[] => {
  return uniqBy(
    [
      entertainer.avatarId && entertainer.avatar
        ? { mediaId: entertainer.avatarId, url: entertainer.avatar, meta: {} }
        : null,
      ...(entertainer.featuredMedia?.map((media) => ({
        mediaId: media._id,
        url: media.image,
        meta: media.meta,
      })) ?? []),
    ]
      .filter((image) => image?.mediaId && image?.url)
      .map((image) => image!),
    'url',
  );
};

const ASPECT_RATIO_MAP = {
  '4/5': 4 / 5,
  '1/1': 1 / 1,
};

export interface QuotePhotoOption {
  mediaId: string;
  url: string;
  meta: {
    photographerName?: string;
    photographerInstagramHandle?: string;
  };
}

const ImageSelector: FC<{
  disableUpload?: boolean;
  images: QuotePhotoOption[];
  uploadedImage?: string | ArrayBuffer;
  setUploadedImage: (image: string | ArrayBuffer) => void;
  onSelect: (image: QuotePhotoOption | undefined, blob?: Blob) => void;
  aspectRatio?: '4/5' | '1/1';
}> = ({
  disableUpload = false,
  images,
  uploadedImage,
  setUploadedImage,
  onSelect,
  aspectRatio = '4/5',
}) => {
  const IMAGE_WIDTH = 120;

  const handleClick = (image: QuotePhotoOption) => {
    onSelect(image);
  };

  const imageUploadField = (
    <ImageUploadField
      cropAspectRatio={ASPECT_RATIO_MAP[aspectRatio]}
      placeholder="Upload photo"
      defaultValue={uploadedImage?.toString()}
      onChange={setUploadedImage}
      py="py-0"
      style={uploadedImage ? { height: '100%' } : {}}
      imgStyle={{ width: 120, aspectRatio }}
    />
  );

  if (uploadedImage) {
    return <div style={{ height: 200, position: 'relative' }}>{imageUploadField}</div>;
  }

  return (
    <HorizontalScroll
      items={[
        <div className="d-flex gap-1 px-1">
          {!uploadedImage &&
            images.map((image) => (
              <div
                key={image.url}
                style={{ width: IMAGE_WIDTH, aspectRatio }}
                onClick={() => {
                  handleClick(image);
                }}
              >
                <AsyncImage
                  src={image.url}
                  style={{
                    display: 'block',
                    height: 'auto',
                    width: '100%',
                    objectFit: 'cover',
                    aspectRatio,
                  }}
                />
              </div>
            ))}
          {!disableUpload && (
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ width: IMAGE_WIDTH, aspectRatio, background: 'rgba(255, 255, 255, 0.1)' }}
            >
              {imageUploadField}
            </div>
          )}
        </div>,
      ]}
    />
  );
};

interface EntertainerQuotePopupContentProps<QuoteSource> {
  onClose: () => void;
  entertainerId: string;
  review: EntertainerQuoteReview<QuoteSource>;
  photoOptions: QuotePhotoOption[];
  onCreate: (
    payload: CreateEntertainerQuotePayload<QuoteSource>,
  ) => Promise<{ quote?: { _id: string } } | undefined>;
  onUpdate: (payload: UpdateEntertainerQuotePayload<QuoteSource>) => Promise<void>;
}

interface EntertainerQuotePopupProps<
  QuoteSource,
> extends EntertainerQuotePopupContentProps<QuoteSource> {
  isVisible: boolean;
}

const PopupContent = <QuoteSource extends unknown>({
  onClose,
  entertainerId,
  review,
  photoOptions,
  onCreate,
  onUpdate,
}: EntertainerQuotePopupContentProps<QuoteSource>) => {
  const { curatedQuote } = review;
  const [quote, setQuote] = useState(curatedQuote?.quote ?? review.text);
  const [quoteByEntertainerName, setQuoteByEntertainerName] = useState(
    curatedQuote?.quoteByEntertainerName ?? review.quoteByEntertainer?.title ?? '',
  );
  const [quoteByEntertainerPhotoMediaId, setQuoteByEntertainerPhotoMediaId] = useState(
    curatedQuote?.quoteByEntertainerPhotoMediaId,
  );
  const [photoCredits, setPhotoCredits] = useState(curatedQuote?.photoCredits ?? '');
  const [photoMediaId, setPhotoMediaId] = useState(curatedQuote?.photoMediaId);
  const [uploadedImage, setUploadedImage] = useState<string | ArrayBuffer>();
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async () => {
    setIsSaving(true);

    try {
      if (review.quoteId) {
        await onUpdate({
          quoteId: review.quoteId,
          quote,
          quoteByEntertainerId:
            curatedQuote?.quoteByEntertainerId ?? review.quoteByEntertainer?._id,
          quoteByEntertainerName,
          quoteByEntertainerPhotoMediaId,
          photoCredits,
          photoMediaId,
          photo: uploadedImage?.toString(),
          source: review.source,
        });
      } else {
        const created = await onCreate({
          entertainerId,
          quote,
          quoteByEntertainerId: review.quoteByEntertainer?._id,
          quoteByEntertainerName,
          quoteByEntertainerPhotoMediaId,
          photoCredits,
          photoMediaId,
          source: review.source,
        });

        if (created?.quote?._id && uploadedImage) {
          await onUpdate({
            quoteId: created.quote._id,
            photo: uploadedImage?.toString(),
          });
        }
      }
    } finally {
      setIsSaving(false);
    }

    onClose();
  };

  const quoteByEntertainerPhotoOptions = review.quoteByEntertainer
    ? transformEntertainerToQuotePhotoOptions(review.quoteByEntertainer)
    : [];

  return (
    <div>
      <TitleToolbar text={review.quoteId ? 'Edit Quote' : 'Create Quote'} size="sm" />

      <div className="mb-2">
        <ImageSelector
          images={photoOptions}
          uploadedImage={uploadedImage}
          setUploadedImage={setUploadedImage}
          onSelect={(image) => {
            setPhotoCredits(
              image?.meta?.photographerInstagramHandle ?? image?.meta?.photographerName ?? '',
            );
            setPhotoMediaId(image?.mediaId);
          }}
        />
      </div>

      <Input
        placeholder="Photo credits"
        value={photoCredits}
        onChange={(e) => setPhotoCredits(e.target.value)}
        className="mb-2"
      />

      <div className="mb-2">
        <Textarea placeholder="Quote" defaultValue={quote} onChange={setQuote} rows={4} />
      </div>

      <div className="mb-2">
        <ImageSelector
          disableUpload
          images={quoteByEntertainerPhotoOptions}
          aspectRatio="1/1"
          setUploadedImage={() => {}}
          onSelect={(image) => {
            setQuoteByEntertainerPhotoMediaId(image?.mediaId);
          }}
        />
      </div>

      <Input
        placeholder="Quoted by"
        value={quoteByEntertainerName}
        onChange={(e) => setQuoteByEntertainerName(e.target.value)}
        className="mb-2"
      />

      <Button
        className="mt-2"
        disabled={isSaving || !quote.trim() || (!photoMediaId && !uploadedImage)}
        onClick={handleSubmit}
      >
        Save
      </Button>
    </div>
  );
};

export const EntertainerQuotePopup = <QuoteSource extends unknown>({
  isVisible,
  ...props
}: EntertainerQuotePopupProps<QuoteSource>) => {
  return (
    <Popup height="full" isVisible={isVisible} onClose={props.onClose}>
      <Popup.Content>{isVisible && <PopupContent {...props} />}</Popup.Content>
    </Popup>
  );
};
